import { RewardStatus } from 'core/enums/rewards';
import { getCurrentLevel, getNextLevel } from 'core/enums/rewards-levels';
import { CompetitionHistoryBase } from 'core/models/red-oxygen/claim-history';
import { CompetitionMeta, CompetitionsBase, RewardBase, RewardMeta } from 'core/models/red-oxygen/get-tier';
import { StarCalc } from 'core/models/red-oxygen/star-calc';
import { RewardStatsDTO } from 'core/models/rewards/reward.dto';
import { EarnMoreStarsStats, Link, Reward, RewardCard, RewardType, StarCategory } from 'core/models/rewards/rewards';
import { rewardsConfig } from '../../environments/rewards';

const userMonthsToTenureMapping: Record<number, number> = {
  60: rewardsConfig.tenure5YearsOrLess,
  120: rewardsConfig.tenure6to10Years,
  180: rewardsConfig.tenure11to15Years,
  240: rewardsConfig.tenure16to20Years,
};

const userMonthsToTenureMappingKeys = Object.keys(userMonthsToTenureMapping);

export class RewardUtils {
  static GetRewardBase = (item: CompetitionHistoryBase | CompetitionsBase | RewardBase) => {
    return 'rewardId' in item && !('entryDate' in item) ? item : null;
  };

  static GetCompetitionBase = (item: CompetitionHistoryBase | CompetitionsBase | RewardBase) => {
    return 'competitionName' in item && !('entryDate' in item) ? item : null;
  };

  static GetCompetitionHistoryBase = (item: CompetitionHistoryBase | CompetitionsBase | RewardBase) => {
    return 'entryDate' in item ? item : null;
  };

  // NOTE: showWithoutContent is helpful for debugging & finding missing reward content.
  static MapRewardContentAndData(
    data: (CompetitionHistoryBase | CompetitionsBase | RewardBase)[],
    content: Reward[],
    userLevel?: string,
    isActive?: boolean,
    rewardsMetaData?: { [key: string]: RewardMeta | CompetitionMeta },
    showWithoutContent?: boolean
  ): RewardCard[] {
    return data
      .map((reward) => {
        const rewardBase = RewardUtils.GetRewardBase(reward);
        const competitionBase = RewardUtils.GetCompetitionBase(reward);
        const competitionHistoryBase = RewardUtils.GetCompetitionHistoryBase(reward);

        const rewardId = getRewardID(reward);

        const matchingContent = content.find(
          (item: Reward) => rewardId === item.promotionCode && (!userLevel || item.rewardLevel.includes(userLevel))
        );
        
        const hasContent = matchingContent || showWithoutContent;

        if (!hasContent) {
          return null;
        }

        let matchingMetaData: RewardMeta | CompetitionMeta | undefined;

        if (rewardId) {
          matchingMetaData = rewardsMetaData?.[rewardId];
        }

        return {
          name: rewardBase?.name || competitionBase?.competitionName || competitionHistoryBase?.competitionName,
          agreementId: getAgreementId(reward),
          isActive: isActive !== undefined ? isActive : getStatus(reward) !== RewardStatus.Expired,
          offerId: getOfferId(reward),
          id: matchingContent?.promotionCode,
          image: matchingContent?.backgroundImage,
          status: getStatus(reward),
          type: getType(reward) || getType(matchingContent),
          subType: getSubType(reward),
          entryDate: getEntryDate(reward),
          button: getButtonText(reward),
          availableFromDate: getAvailableFromDate(reward),
          competitionEntryId: getCompetitionEntryId(reward),
          requiresAgreement: rewardBase?.requiresAgreement,
          description: {
            header: matchingContent?.title,
            label: matchingContent?.labelIcon,
            text: matchingContent?.descriptor,
            details: matchingContent?.bodyText,
            termsAndConditionsLink: getLink(matchingContent?.termsAndConditions),
            actionLink: getLink(matchingContent?.actionLink),
            endDate: getEndDate(reward),
            // TODO: fix once value is added to competitions
            value: getValue(reward),
            noOfEntries: getNoOfEntries(reward),
            claimedMessage: matchingContent?.claimedMessage,
          }, 
          premiumExclusive: matchingMetaData?.premiumExclusive,
          premiumExperience: matchingMetaData?.premiumExperience,
          hideInWeb: matchingMetaData?.hideInWeb,
        } as RewardCard;
      })
      .filter((item: RewardCard | null) => item !== null) as RewardCard[];
  }

  static MapRewardContentOld(data: Reward[]): RewardCard[] {
    // TODO: Add reward type filtering based on Type property
    return data
      .filter((el) => el.icon)
      .map((el) => {
        const reward = {} as RewardCard;
        reward.id = el.promotionCode;
        reward.image = el.backgroundImage;
        reward.icon = el.icon;
        reward.description = {
          header: el.title,
          text: el.descriptor,
          endDate: new Date(),
        };
        return reward;
      });
  }

  static MapRewardContent(data: RewardType[]): RewardCard[] {
    // TODO: Add reward type filtering based on Type property
    return data
      .filter((el) => el.icon)
      .map((el) => {
        const reward = {} as RewardCard;
        reward.image = el.backgroundImage;
        reward.icon = el.icon;
        reward.description = {
          header: el.header,
          text: el.description,
          endDate: new Date(),
        };
        return reward;
      });
  }

  static SortRewardsByDate(rewards: (CompetitionHistoryBase | CompetitionsBase | RewardBase)[]) {
    const getDate = (item: CompetitionHistoryBase | CompetitionsBase | RewardBase) => {
      if ('endDate' in item) {
        return item.endDate;
      }

      if ('claimedDate' in item && item.claimedDate) {
        return item.claimedDate;
      }

      if ('entryDate' in item) {
        return item.entryDate;
      }

      return item.expiryDate;
    };
    return rewards.sort((a, b) => getDate(b) - getDate(a));
  }

  static amountOfStars(
    data: Reward,
    stats?: EarnMoreStarsStats,
    statsBase?: RewardStatsDTO,
    principalPackage?: string
  ): number | undefined {
    if (stats) {
      if (data?.promotionCode === 'tenure' && stats.tenure) {
        const userMonths = statsBase?.tenure_RewardsTenure ? parseInt(statsBase?.tenure_RewardsTenure) : 0;

        if (!userMonths) {
          return;
        }

        const keys = userMonthsToTenureMappingKeys;

        for (const key of keys) {
          const mths = parseInt(key);

          if (userMonths <= mths) {
            return userMonthsToTenureMapping[userMonths];
          }

          if (userMonths >= 241) {
            return rewardsConfig.tenure21YearsPlus;
          }
        }
      }

      if (data?.promotionCode === 'decoderInsurance' && stats.decoderInsurance) {
        let total = 0;
        if (statsBase?.debt_Waiver_Insurance ? parseInt(statsBase?.debt_Waiver_Insurance) : 0) {
          total += rewardsConfig.debtWaiverInsurance;
        }

        if (statsBase?.subscription_Waiver_Insurance ? parseInt(statsBase?.subscription_Waiver_Insurance) : 0) {
          total += rewardsConfig.subscriptionWaiverInsurance;
        }

        if (statsBase?.funeral_Insurance ? parseInt(statsBase?.funeral_Insurance) : 0) {
          total += rewardsConfig.funeralInsurance;
        }

        if (
          statsBase?.number_of_Active_Decoder_Insurances
            ? parseInt(statsBase?.number_of_Active_Decoder_Insurances) > 0
            : 0
        ) {
          total += rewardsConfig.numberOfActiveDecoderInsurances;
        }
        return total;
      }
    }
    const umbracoValue = data?.principlePackages?.find(
      (pkg) => pkg.packageName.replace(/za/gi, '').trim() === principalPackage
    )?.stars;
    return umbracoValue;
  }

  static toEarnStars(
    data: Reward,
    stats?: EarnMoreStarsStats,
    statsBase?: RewardStatsDTO,
    principalPackage?: string
  ): number | undefined {
    if (data?.promotionCode === 'tenure') {
      return rewardsConfig.tenure21YearsPlus;
    }
    if (data?.promotionCode === 'decoderInsurance') {
      return rewardsConfig.insuranceMaxStars;
    }
    if (data?.promotionCode === 'contract') {
      const premiumValue = data?.principlePackages?.find(
        (pkg) => pkg.packageName.replace(/za/gi, '').trim() === rewardsConfig.contractMaxStarsPackage
      )?.stars;
      return premiumValue; // collect highest potential value
    }
    if (data?.promotionCode === 'package') {
      const premiumValue = data?.principlePackages?.find(
        (pkg) => pkg.packageName.replace(/za/gi, '').trim() === rewardsConfig.packageMaxStarsPackage
      )?.stars;
      return premiumValue; // collect highest potential value
    }
    const umbracoValue = data?.principlePackages?.find(
      (pkg) => pkg.packageName.replace(/za/gi, '').trim() === principalPackage
    )?.stars;
    return umbracoValue; // return current package value - default
  }

  static checkToDisable(data: Reward, stats?: EarnMoreStarsStats): boolean | undefined {
    if (data?.promotionCode === 'streama') {
      return stats?.exploraUltra;
    }
    if (data?.promotionCode === 'explora') {
      return stats?.exploraUltra || stats?.streama;
    }
    return false; // default display item
  }

  static MapStatCalculations(rewardData: Reward[], stats: StarCalc, maxStats: StarCalc) {
    const { statCategories } = rewardsConfig;
    const getStars = RewardUtils.GetStarMappingFromStats(rewardData, stats, maxStats);

    return {
      stayConnected: getStars(statCategories.stayConnected),
      productUsage: getStars(statCategories.productUsage),
      watchTime: getStars(statCategories.watchTime),
      selfServiceUsage: getStars(statCategories.selfServiceUsage),
      other: getStars(statCategories.other),
    };
  }

  static GetStarMappingFromStats(rewardData: Reward[], stats: StarCalc, maxStats: StarCalc) {
    return (properties: string[]) => {
      return properties.reduce(
        (category: StarCategory, property) => {
          const calc = (stats.hasOwnProperty(property) && (stats as any)[property]) ?? 0;
          const maxCalc = (maxStats.hasOwnProperty(property) && (maxStats as any)[property]) ?? 0;
          const starsRemaining = maxCalc - calc;
          const rewardItem = rewardData.find((reward) => reward.rewardId === property) || ({} as Reward);

          const starValues = {
            name: property,
            starsToEarn: starsRemaining,
            earnedAmount: calc,
            earnStars: maxCalc,
          };

          const previousRewards = category.items;
          const currentReward = {
            ...starValues,

            actionText: rewardItem.tagLabel,
            actionLink: getLink(rewardItem.actionLink),

            item: {
              image: rewardItem.logo,
              icon: rewardItem.icon,
              name: rewardItem.tagLine,
              percentage: rewardItem.descriptor,
              achieved: starsRemaining === 0,
              disabled: false,
            },
            details: {
              image: rewardItem.backgroundImage,
              description: {
                header: rewardItem.title,
                text: rewardItem.tagLabelLead,
                details: rewardItem.bodyText,
              },
              button: {
                text: rewardItem.tagLabel,
              },
            },
          };

          return {
            totalStarValue: category.totalStarValue + starValues.earnedAmount,
            totalMaxValue: category.totalMaxValue + starValues.earnStars,
            totalStarsToEarn: category.totalStarsToEarn + starValues.starsToEarn,

            principlePackages: rewardItem?.principlePackages?.map((pkg) => {
              return { name: pkg.packageName.replace(/za/gi, ''), stars: pkg.stars };
            }),

            items: [...previousRewards, { ...currentReward }],
          };
        },
        {
          totalStarValue: 0,
          totalMaxValue: 0,
          totalStarsToEarn: 0,
          items: [],
        } as StarCategory
      );
    };
  }

  static MapRewardsPremiumContent(
    isMAH: boolean,
    data: Reward[],
    achievedCondition: (value: Reward) => boolean,
    stats: any,
    statsBase?: RewardStatsDTO,
    principalPackage?: string
  ) {
    return data
      .filter((item) => item && Object.keys(item).length > 0)
      .map((rewardItem) => {
        return {
          actionText: rewardItem.tagLabel,
          actionLink: getLink(rewardItem.actionLink),
          // TODO: Replace completely with amount of stars once all the rewards have principle packages in umbraco
          earnedAmount: isMAH
            ? stats[rewardItem.rewardId]
            : this.amountOfStars(rewardItem, stats, statsBase, principalPackage),
          earnStars: isMAH
            ? parseInt(rewardItem.descriptor)
            : this.toEarnStars(rewardItem, stats, statsBase, principalPackage), //amountOfStars || parseInt(rewardItem.LabelIcon),
          principlePackages: rewardItem?.principlePackages?.map((pkg) => {
            return { name: pkg.packageName.replace(/za/gi, ''), stars: pkg.stars };
          }),
          item: {
            image: rewardItem.logo,
            icon: rewardItem.icon,
            name: rewardItem.tagLine,
            percentage: rewardItem.descriptor,
            achieved: achievedCondition(rewardItem),
            disabled: this.checkToDisable(rewardItem, stats),
          },
          details: {
            image: rewardItem.backgroundImage,
            description: {
              header: rewardItem.title,
              text: rewardItem.tagLabelLead,
              details: rewardItem.bodyText,
            },
            button: {
              text: rewardItem.tagLabel,
            },
          },
        };
      });
  }

  static GetAllRewardsStatuses(stats: RewardStatsDTO): EarnMoreStarsStats {
    const item = getZeroAndOneStringsAsBooleans(stats);
    return {
      dstvNow: item.has_Used_Dstv_Now_Last_3_Months_Indicator || item.is_Streaming,
      package: !!item.preferred_Package,
      contract: item.has_Subscription_Pricelock || item.has_Pricelock,
      movieBundle: item.movie_Bundle || item.has_Indian,
      xtraViewPvr: item.has_Xtraview || item.has_Pvr,
      marketingOptIn: item.market_Optin,
      debitOrder: item.debit_Order,
      threeMonthsActive: item.no_Dormantdays_3m,
      streaming: false, //!!item.contract_Term,
      streama: item.highest_Tiered_Device === 'Streama',
      decoderInsurance:
        item.number_of_Active_Decoder_Insurances ||
        item.subscription_Waiver_Insurance ||
        item.funeral_Insurance ||
        item.debt_Waiver_Insurance,
      explora: item.highest_Tiered_Device === 'Explora',
      exploraUltra: item.highest_Tiered_Device === 'Explora UHD',
      boxOffice: item.has_Used_Boxoffice_Last_3_Months_Indicator,
      addOnIndian: item.has_Indian,
      connectedExplora: item.connectedExplora_3m,
      showMax: item.has_Used_Showmax_Last_3_Months_Indicator,
      dstvInternet: item.has_Internet,
      tenure: !!item.tenure_RewardsTenure,
      setupDebitOrder: false, //item.has_Used_Showmax_Last_3_Months_Indicator,
      disneyPlus: item.has_Disney_Plus,
    } as EarnMoreStarsStats;
  }

  static GetReducedCompetitionDuplicates(competitions: CompetitionHistoryBase[] = []): CompetitionHistoryBase[] {
    const reduced = competitions.reduce((accumulator: Map<string, CompetitionHistoryBase>, current) => {
      const { competitionName, entriesCount } = current;

      const hasExisting = accumulator.has(competitionName);
      const competition = accumulator.get(competitionName) || current;

      const updatedCount = !hasExisting ? entriesCount : (competition.entriesCount += entriesCount);

      accumulator.set(competitionName, { ...competition, entriesCount: updatedCount, competitionName });

      return accumulator;
    }, new Map<string, CompetitionHistoryBase>());

    //NOTE: MapIterable.toArray() is not supported in older browsers - new in 2025
    //return reduced.values().toArray();
    return Array.from(reduced.values());
  }

  static GetTopLabel(endDate: Date) {
    const oneDay = 86400000;
    const oneWeek = 604800000;

    if (this.GetIsExpired(endDate) || getTimeLeft(endDate) > oneWeek) {
      return;
    }

    if (getTimeLeft(endDate) < oneDay) {
      return { text: 'rewards.card.topLabel.oneDay', color: '#D20000' };
    }

    return { text: 'rewards.card.topLabel.dontMiss', color: '#0592DB' };
  }

  static GetIsExpired(endDate: Date) {
    return getTimeLeft(endDate) < 0 ? true : false;
  }

  static FilterOutEnteredCompetitions(activeCompetitions: RewardCard[], enteredCompetitions: RewardCard[]) {
    return activeCompetitions.filter((obj1) => {
      return !enteredCompetitions.find((obj2) => obj1.id === obj2.id);
    });
  }

  static FilterOutClaimedRewards(availableRewards: RewardCard[], claimedRewards: RewardCard[]) {
    return availableRewards.filter((obj1) => {
      return !claimedRewards.find((obj2) => obj1.id === obj2.id);
    });
  }

  static GetRewardsLevel(starsCount: number) {
    return getCurrentLevel(starsCount);
  }

  static GetStarsNeededForNextLevel(starsCount: number) {
    const nextLevel = getNextLevel(starsCount);

    const amount = nextLevel.minimum - starsCount;

    return amount < 0 ? 0 : amount;
  }

  static GetRewardsNextLevel(starsCount: number) {
    return getNextLevel(starsCount).name;
  }
}

const getTimeLeft = (date: Date) => {
  return date.getTime() - new Date().getTime();
};

const getEndDate = (reward: any) => {
  return reward.expiryDate
    ? new Date(parseInt(reward.expiryDate))
    : reward.endDate
    ? new Date(parseInt(reward.endDate))
    : null;
};

const getCompetitionEntryId = (reward: any): string | undefined => {
  return reward.competitionEntryId || null;
};

const getValue = (reward: any): string | undefined => {
  return reward.value;
};

const getAgreementId = (reward: any): string | undefined => {
  return reward.agreementId || null;
};

const getOfferId = (reward: any): string | undefined => {
  return reward.offerId || null;
};

const getType = (reward: any): string | undefined => {
  return reward.type || null;
};

const getSubType = (reward: any): string | undefined => {
  return reward.subType || null;
};

const getNoOfEntries = (reward: any) => {
  return reward?.entriesCount ? parseInt(reward?.entriesCount) : null;
};

const getStatus = (reward: any): string | undefined => {
  return reward.status;
};

const getEntryDate = (reward: any): string | undefined => {
  return reward.entryDate;
};

const getRewardID = (reward: RewardBase | CompetitionHistoryBase | CompetitionsBase): string | undefined => {
  return 'competitionName' in reward ? reward.competitionName : reward.rewardId;
};

const getZeroAndOneStringsAsBooleans = (obj: RewardStatsDTO) => {
  const valuesAsBooleans = {} as Record<string, string | boolean>;
  for (const key in obj) {
    const statsValue = obj[key as keyof RewardStatsDTO];
    const valueAsInt = parseInt(statsValue);
    valuesAsBooleans[key] = !isNaN(valueAsInt) && valueAsInt <= 1 ? !!valueAsInt : statsValue;
  }
  return valuesAsBooleans;
};

const getButtonText = (card: any) => {
  if (card.entryDate && card.status !== RewardStatus.Expired) {
    return { text: 'buttons.entered' };
  }

  if (card.status === RewardStatus.Claimed) {
    return { text: 'buttons.claimed', icon: 'icon-tick' };
  }

  return { text: 'buttons.expired' };
};

const getAvailableFromDate = (reward: any): number | null => {
  const timeStamp = +reward.availableFromDate;
  const timeStampNow = new Date().getTime();
  return timeStamp > timeStampNow ? timeStamp : null;
};

const getLink = (link?: Link) => {
  if (!link) {
    return null;
  }

  link.target = link.target ?? '_self';
  return link;
};